import { CenterPanelHelpDetails } from 'app/center/center_panel.service';
import { PLACEMENT_BOTTOM, PLACEMENT_TOP } from 'app/shared/help_and_information/tours/TourService';
import { HelpDetails } from 'app/shared/help_and_information/HelpRegistry';
import $ from 'jquery';

export const ON_CLICK_HELP_KEY = 'on-click-key';
export const EDIT_TRANSMITTER_HELP_KEY = 'edit-transmitter-key';

export const rfPlanningPanelHelpDetails: CenterPanelHelpDetails = {
    header: 'RF Planning Panel',
    tags: ['RF Planning', 'Center'],
    quickStartGuideSearchText: `qsg-rf-planning`,
    descriptionHtml: `<p>The <span class="ims-name">RF Planning</span> tool gives the user the ability to make RF predictions based on the 3D terrain data.</p>
<p><span class="ims-name">RF Plans</span> are collections of <span class="ims-name">RF Transmitters</span>. Each <span class="ims-name">RF Transmitter</span> 
can be individually configured, with a large number of parameters able to be modified.</p>
<p>There are 4 different coverage types available, these determine the colouring of the generated RF Plans. </p>
<p>
    <ul>
    <li><p><strong>RSSI</strong> - This generates a 'stepped' RSSI map with three colour tiers.</p>
    </li>
    <li><p><strong>RSSI Gradient</strong> - This generates a 'gradient' RSSI map with a smooth transition of RSSI colours.</p>
    </li>
    <li><p><strong>Overlaps</strong> - This is coloured by frequency/channel and is black where there is an overlap of any RF.</p>
    </li>
    <li><p><strong>Same Channel Overlaps</strong> - This is coloured by frequency/channel and is black where there is an overlap of RF of the <u>same frequency</u>.</p>
    </li>
    </ul>
</p>
<p>Click the <span class="glyphicon glyphicon-align-justify"></span> legend icon next to a generated 
<span class="ims-name">coverage layer</span> to see what the colours for the above coverage types represent.</p>



<p>See the IMS Wiki for a more detailed description of <span class="ims-name">RF Planning</span>.</p>`,
    innerTourSteps: [
        {
            title: 'On-Click Mode',
            content: `
                <p>Toggles <span class="ims-name">On-Click Mode</span> which allows the placing of one or more simulated
                RF transmitters. Each placed <span class="ims-name">RF transmitter</span> parameters are set via the 
                <span class="ims-name">Edit On-Click Transmitter</span> button.
                </p>
            `,
            target: 'center-rf-plan-on-click-planning',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Edit On-Click Transmitter',
            content: `
                <p>The <span class="ims-name">Edit On-Click Transmitter</span> button will open a popup
                that allows setting of the transmitter parameters for <span class="ims-name">On-Click Mode</span>.
                </p>
            `,
            target: 'center-rf-plan-on-click-template',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Create New RF Plan',
            content: `
                <p>The <span class="ims-name">Create New RF Plan</span> button creates a new 
                <span class="ims-name">RF Plan</span>. An <span class="ims-name">RF Plan</span> is a collection 
                of <span class="ims-name">RF Transmitters</span>.</p>
            `,
            target: 'center-rf-plan-create-new-rf-plan',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'List of RF Plans & Coverage Layers',
            content: `<p>This is the list of <span class="ims-name">RF Plans</span> and their generated <span class="ims-name">coverage layers</span>.</p>
            <p>You can delete <span class="ims-name">RF Plans</span> and <span class="ims-name">coverage layers</span> 
            using the <span class="glyphicon glyphicon-trash"></span> button, and open the legend on a generated
            <span class="ims-name">coverage layer</span> using the <span class="glyphicon glyphicon glyphicon-align-justify"></span> button.</p>
            <p>A <span class="ims-name">coverage layer</span> can be hidden using the <span class="glyphicon glyphicon-eye-open"></span> button.</p>`,
            target: 'center-rf-plan-tree',
            placement: PLACEMENT_BOTTOM,
        },
        {
            title: 'Coverage Layer Type Selector',
            content: `<p>You can select one of four coverage layer types in this section.</p>
                     <p>Coverage layer types decide the colouring of your <span class="ims-name">RF Plan's</span> 
                     generated coverage layer.</p>
                     <p>For example, the 'Overlaps' option is coloured by frequency and designed to highlight any 
                     overlaps in the RF plan (coloured black), whereas RSSI is intended to simply show you the expected 
                     signal strength at that location.</p>
                     `,
            target: 'center-rf-plan-coverage-type-selector',
            placement: PLACEMENT_TOP,
        },
        {
            title: 'Generate Coverage',
            content: `<p>The <span class="ims-name">Generate Coverage</span> button creates a coverage layer for the selected 
                        <span class="ims-name">RF Plan</span> with the chosen 
                        <span class="ims-name">Coverage Layer Type.</span></p>`,
            target: 'center-rf-plan-generate',
            placement: PLACEMENT_TOP,
        },
        {
            title: 'Bulk Create Transmitters',
            content: `<p>This button lets you generate <span class="ims-name">RF Transmitters</span> based on
                        <span class="ims-name">Assets</span> or <span class="ims-name">IP Devices</span> and uses the 
                        parameters set in the transmitter template for new transmitters.</p>`,
            target: 'center-rf-plan-auto-add',
            placement: PLACEMENT_TOP,
        },
        {
            title: 'Quick Edit Transmitters',
            content: `<p>This button lets you edit existing <span class="ims-name">RF Transmitters</span>, or add new 
                     ones to the map.</p>
                     <p><b>When in edit mode:</b></p>
                     <p><u>Left clicking</u> on a blank area on the map will create a new RF Transmitter.</p>
                     <p><u>Right clicking</u> on an RF Transmitter will delete it.</p>
                     <p><u>Dragging</u> an RF Transmitter will move it.</p>`,
            target: 'center-rf-plan-edit-mode',
            placement: PLACEMENT_TOP,
        },
        {
            title: 'Edit Transmitter Template',
            content: `<p>This edits the <span class="ims-name">RF Transmitter</span> template.</p>
                      <p>The template is copied whenever you use the <span class="ims-name">Add/Edit Transmitters</span>
                      button to add a new <span class="ims-name">RF Transmitter</span> to the map.</p>
                      <p>This can be very useful if you need to place several RF Transmitters of a particular 
                      specification to the map.</p>`,
            target: 'center-rf-plan-edit-transmitter-template',
            placement: PLACEMENT_TOP,
        },
        {
            title: 'Transmitter List',
            content: `<p>This displays the list of <span class="ims-name">RF Transmitters</span> in the selected 
                      <span class="ims-name">RF Plan</span>.</p>`,
            target: 'center-rf-plan-transmitter-list',
            placement: PLACEMENT_TOP,
        },
    ],
    beforeStart: () => {
        $('.rf-plan.list-item > .help-tour-click').click();
        return 250;
    },
};

export const onClickPanelHelpDetails: HelpDetails = {
    key: ON_CLICK_HELP_KEY,
    header: 'RF Planning On-Click Mode',
    tags: ['RF Planning', 'On-Click Mode'],
    quickStartGuideSearchText: 'qsg-rf-planning',
    descriptionHtml: `<p>The <span class="ims-name">On-Click Mode</span> button will change the cursor mode to
    place a single transmitter RF plan on the map, at any map point where the cursor is clicked.
    </p>`,
    tourType: null,
    innerTourSteps: [],
};

export const editTransmitterHelpDetails: HelpDetails = {
    key: EDIT_TRANSMITTER_HELP_KEY,
    header: 'Add/Edit Transmitters',
    tags: ['RF Planning'],
    quickStartGuideSearchText: 'qsg-rf-planning',
    descriptionHtml: `<p>This button lets you edit existing <span class="ims-name">RF Transmitters</span>, or add new 
    ones to the map.</p>
    <p><b>When in edit mode:</b></p>
    <p><u>Left clicking</u> on a blank area on the map will create a new RF Transmitter.</p>
    <p><u>Right clicking</u> on an RF Transmitter will delete it.</p>
    <p><u>Dragging</u> an RF Transmitter will move it.</p>`,
    tourType: null,
    innerTourSteps: [],
};
